/**
 * Window scroll helpers that nudge elements into view with viewport padding.
 * Uses `window.scrollBy` (not `Element.scrollIntoView`) so behavior stays explicit
 * and is not confused with browser focus scrolling.
 *
 * - {@link scrollElementIntoViewWithInset} — primitive (any element).
 * - {@link scrollActiveFormControlIntoViewIfInContainer} — focused control inside a root.
 *   Call after `focus({ preventScroll: true })`, optionally after a short delay
 *   (`ORDER_FORM_SCROLL_FOCUS_FOLLOW_MS` in `orderFormAccordionConstants.js`).
 */

/** Default padding from viewport edges (px). */
export const DEFAULT_FOCUS_SCROLL_INSET_PX = 72

/**
 * Default delay (ms) used when `focusElement(..., { delay: true })`.
 * This matches the order-form accordion “focus follow” timing.
 */
export const DEFAULT_FOCUS_SCROLL_DELAY_MS = 400

const FORM_CONTROL_TAGS = new Set(["INPUT", "SELECT", "TEXTAREA", "BUTTON"])

/**
 * Prefer a real focusable control. Error `domId`s sometimes point at a wrapper
 * (e.g. review-terms, license-photos); focus the first nested control when needed.
 * @param {Element | null | undefined} root
 * @returns {HTMLElement | null}
 */
export function resolveFocusableElement(root) {
    if (!(root instanceof HTMLElement)) {
        return null
    }
    if (FORM_CONTROL_TAGS.has(root.tagName)) {
        if (
            (root instanceof HTMLInputElement ||
                root instanceof HTMLSelectElement ||
                root instanceof HTMLTextAreaElement ||
                root instanceof HTMLButtonElement) &&
            root.disabled
        ) {
            // Fall through to a nested control if present.
        } else {
            return root
        }
    } else if (root.tabIndex >= 0) {
        return root
    }
    const nested = root.querySelector(
        'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    return nested instanceof HTMLElement ? nested : root
}

/**
 * Focus a specific element (using `preventScroll`) and optionally scroll it into
 * view with an inset after a delay.
 *
 * Use this when the component knows which element should be focused, and you
 * want consistent "focus then gentle scroll" behavior across the app.
 *
 * @param {HTMLElement | null | undefined} el
 * @param {{
 *   enabled?: boolean,
 *   preventScroll?: boolean,
 *   delay?: boolean,
 *   delayMs?: number,
 *   insetPx?: number,
 *   behavior?: "auto" | "instant" | "smooth",
 *   scrollIntoView?: boolean,
 * }} [opts]
 * @returns {boolean} Whether focus was applied
 */
export function focusElement(el, opts = {}) {
    const target = resolveFocusableElement(el)
    if (!(target instanceof HTMLElement)) {
        return false
    }
    const enabled = opts.enabled !== false
    if (!enabled) {
        return false
    }

    const preventScroll = opts.preventScroll !== false
    const delayMs =
        opts.delayMs != null ? opts.delayMs
            : opts.delay ? DEFAULT_FOCUS_SCROLL_DELAY_MS
                : 0
    const insetPx = opts.insetPx ?? DEFAULT_FOCUS_SCROLL_INSET_PX
    const behavior = opts.behavior ?? "smooth"
    const scrollIntoView = opts.scrollIntoView !== false

    target.focus({ preventScroll })
    if (!scrollIntoView) {
        return true
    }
    window.setTimeout(() => {
        scrollElementIntoViewWithInset(target, insetPx, { behavior })
    }, delayMs)
    return true
}

/**
 * Scrolls the window the minimum amount so `el` sits inside the viewport with
 * padding on all sides (avoids hugging edges).
 * @param {HTMLElement} el
 * @param {number} insetPx
 * @param {{ behavior?: "auto" | "instant" | "smooth" }} [opts]
 */
export function scrollElementIntoViewWithInset(el, insetPx, opts = {}) {
    const behavior = opts.behavior ?? "smooth"
    const rect = el.getBoundingClientRect()
    const vh = window.innerHeight
    const vw = window.innerWidth
    let dy = 0
    let dx = 0
    if (rect.top < insetPx) {
        dy = rect.top - insetPx
    } else if (rect.bottom > vh - insetPx) {
        dy = rect.bottom - (vh - insetPx)
    }
    if (rect.left < insetPx) {
        dx = rect.left - insetPx
    } else if (rect.right > vw - insetPx) {
        dx = rect.right - (vw - insetPx)
    }
    if (dy !== 0 || dx !== 0) {
        window.scrollBy({ behavior, top: dy, left: dx })
    }
}

/**
 * If `document.activeElement` is an INPUT / SELECT / TEXTAREA / BUTTON inside
 * `container`, scroll it into view using {@link scrollElementIntoViewWithInset}.
 * @param {HTMLElement} container
 * @param {number} [insetPx]
 * @param {{ behavior?: "auto" | "instant" | "smooth" }} [opts]
 * @returns {boolean} Whether a scroll was applied
 */
export function scrollActiveFormControlIntoViewIfInContainer(
    container,
    insetPx = DEFAULT_FOCUS_SCROLL_INSET_PX,
    opts = {}
) {
    const ae = document.activeElement
    if (!(ae instanceof HTMLElement) || !container.contains(ae)) {
        return false
    }
    if (!FORM_CONTROL_TAGS.has(ae.tagName)) {
        return false
    }
    scrollElementIntoViewWithInset(ae, insetPx, opts)
    return true
}

/**
 * Convenience wrapper for handler flows that do:
 * - focus({ preventScroll: true })
 * - then scroll the focused control into view (optionally after a delay)
 *
 * @param {HTMLElement} container
 * @param {{ delayMs?: number, insetPx?: number, behavior?: "auto" | "instant" | "smooth" }} [opts]
 */
export function scheduleScrollActiveFormControlIntoViewIfInContainer(
    container,
    opts = {}
) {
    const delayMs = opts.delayMs ?? 0
    const insetPx = opts.insetPx ?? DEFAULT_FOCUS_SCROLL_INSET_PX
    const behavior = opts.behavior ?? "smooth"
    window.setTimeout(() => {
        scrollActiveFormControlIntoViewIfInContainer(container, insetPx, { behavior })
    }, delayMs)
}
